import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Input, Tag, Select as AntSelect, DatePicker, Card } from 'antd';
import {
  SearchOutlined,
  UserOutlined,
  DownloadOutlined,
  ReloadOutlined,
  UserAddOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import moment from 'moment';
import Swal from 'sweetalert2';
import { NotificationManager } from 'react-notifications';

import Layout from '../../components/Layout';
import PageHeadingBanner from '../../components/shared/PageHeadingBanner';
import AntDropdown from "../../components/CustomDropdown/AntDropdown";
import { customBaseUrl, httpGet, httpPost, httpPut, httpPostBlob } from '../../store/http';
import { showLoader, hideLoader } from '../../utils/loader';
import CreateUserModal from './CreateUserModal';
import './AllUsers.css';

const { Option } = AntSelect;

const SEARCH_OPTIONS = [
  { value: "phoneNumber", label: "Phone", placeholder: "Enter phone number" },
  { value: "email", label: "Email", placeholder: "Enter email address" },
  { value: "name", label: "Name", placeholder: "Enter name" },
  { value: "userId", label: "User ID", placeholder: "Enter user ID" },
  { value: "bvn", label: "BVN", placeholder: "Enter BVN" },
  { value: "nin", label: "NIN", placeholder: "Enter NIN" },
  { value: "accountNumber", label: "Account Number", placeholder: "Enter user account number" },
  { value: "domain", label: "Domain", placeholder: "Enter domain (e.g. wayabank.ng)" },
];

const USER_TYPES = [
  { value: "", label: "All" },
  { value: "PERSONAL", label: "Individual", userType: "PERSONAL" },
  { value: "CAC_BUSINESS", label: "Registered Corporate", userType: "CORPORATE", businessType: "CAC_BUSINESS" },
  // { value: "NON_CAC_BUSINESS", label: "Non-Registered Corporate", userType: "CORPORATE", businessType: "NON_CAC_BUSINESS" },
];

const userStatuses = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
  { value: "SUSPENDED", label: "Suspended" },
  { value: "CLOSED", label: "Closed" },
];

const kycStatuses = [
  { value: "VERIFIED", label: "Verified" },
  { value: "PENDING", label: "Pending" },
  { value: "REJECTED", label: "Rejected" },
  { value: "UNVERIFIED", label: "Unverified" },
];

const tierLevels = [
  { value: "PERSONAL_TIER_1", label: "Personal Tier 1" },
  { value: "PERSONAL_TIER_2", label: "Personal Tier 2" },
  { value: "PERSONAL_TIER_3", label: "Personal Tier 3" },
  { value: "CAC_TIER_1", label: "CAC Tier 1" },
  { value: "CAC_TIER_2", label: "CAC Tier 2" },
  { value: "CAC_TIER_3", label: "CAC Tier 3" },
  { value: "CAC_TIER_4", label: "CAC Tier 4" },
  { value: "NON_CAC_TIER_1", label: "Non-CAC Tier 1" },
  { value: "NON_CAC_TIER_2", label: "Non-CAC Tier 2" },
  { value: "NON_CAC_TIER_3", label: "Non-CAC Tier 3" },
];

const customerCategories = [
  { value: "A", label: "Category A" },
  { value: "B", label: "Category B" },
  { value: "C", label: "Category C" },
];

const AllUsers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState("");
  const [lockedUserType, setLockedUserType] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const [searchType, setSearchType] = useState("phoneNumber");
  const [dateRange, setDateRange] = useState(null);
  const [status, setStatus] = useState(null);
  const [kycStatus, setKycStatus] = useState(null);
  const [category, setCategory] = useState(null);
  const [tierLevel, setTierLevel] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const type = params.get('type');
    const action = params.get('action');

    if (type === 'individual') {
      setSelectedUserType('PERSONAL');
      setLockedUserType('PERSONAL');
    } else if (type === 'corporate') {
      setSelectedUserType('CAC_BUSINESS');
      setLockedUserType(null);
    } else {
      setLockedUserType(null);
    }
    if (action === 'create') setShowCreateModal(true);
  }, [location.search]);

  const fetchUsers = useCallback(async (params = {}, filterType = null, filterValue = null) => {
    setLoading(true);
    showLoader();

    let url = `${customBaseUrl.authUrl}/admin/users`;
    let isMultiSearch = false;
    let payload = {
      size: params.pageSize || pagination.pageSize,
      page: (params.current || pagination.current) - 1,
      sortBy: "createdAt",
      sortDirection: "DESC"
    };

    const selectedType = USER_TYPES.find(t => t.value === selectedUserType);
    if (selectedType?.userType) {
      payload.userType = selectedType.userType;
      if (selectedType.businessType) payload.businessType = selectedType.businessType;
      isMultiSearch = true;
    }

    if (dateRange) {
      isMultiSearch = true;
      payload.createdDateFrom = dateRange[0].format('YYYY-MM-DDT00:00:00');
      payload.createdDateTo = dateRange[1].format('YYYY-MM-DDT23:59:59');
    }

    if (status) {
      isMultiSearch = true;
      payload.status = status;
    }

    if (kycStatus) {
      isMultiSearch = true;
      payload.kycStatus = kycStatus;
    }

    if (category) {
      isMultiSearch = true;
      payload.customerCategory = category;
    }

    if (tierLevel) {
      isMultiSearch = true;
      payload.tierLevel = tierLevel;
    }

    if (category || tierLevel) {
      url = `${customBaseUrl.authUrl}/admin/users/customers`;
      isMultiSearch = false;
      if (category) payload.customerCategory = category;
      if (tierLevel) payload.tierLevel = tierLevel;
      if (status) payload.status = status;
      if (kycStatus) payload.kycStatus = kycStatus;
      if (dateRange) {
        payload.createdDateFrom = dateRange[0].format('YYYY-MM-DDT00:00:00');
        payload.createdDateTo = dateRange[1].format('YYYY-MM-DDT23:59:59');
      }
      const queryString = new URLSearchParams(payload).toString();
      url = `${url}?${queryString}`;
    } else if (filterType && filterValue) {
      let finalValue = filterValue;
      if (filterType === "phoneNumber") {
        const cleaned = filterValue.replace(/\D/g, "");
        finalValue = `+234${cleaned.slice(-10)}`;
      }

      if (filterType === "name") {
        url = `${url}/search/name?name=${finalValue}&page=${payload.page}&size=${payload.size}`;
        isMultiSearch = false;
      } else if (["phoneNumber", "email", "bvn", "nin", "domain"].includes(filterType)) {
        isMultiSearch = true;
        payload[filterType] = finalValue;
        url = `${url}/search`;
      } else if (["userId", "accountNumber"].includes(filterType)) {
        if (!dateRange && !status && !kycStatus && !selectedUserType) {
          switch (filterType) {
            case "userId": url = `${url}/${finalValue}`; break;
            case "accountNumber": url = `${url}/account/${finalValue}`; break;
          }
        } else {
          isMultiSearch = true;
          payload[filterType] = finalValue;
          url = `${url}/search`;
        }
      } else {
        isMultiSearch = true;
        payload[filterType] = finalValue;
        url = `${url}/search`;
      }
    } else if (isMultiSearch) {
      url = `${url}/search`;
    } else {
      const queryString = new URLSearchParams(payload).toString();
      url = `${url}?${queryString}`;
    }

    try {
      let res;
      if (isMultiSearch) {
        res = await httpPost(url, payload);
      } else {
        res = await httpGet(url);
      }

      if (res?.success) {
        const rawData = res.data;
        let finalUsers = [];
        let total = 0;

        if (category || tierLevel) {
          finalUsers = (rawData.customers || []).map(u => ({
            ...u,
            id: u.userId,
            firstName: u.customerName?.split(' ')[0] || '—',
            lastName: u.customerName?.split(' ').slice(1).join(' ') || '—',
            isActive: u.customerStatus === 'ACTIVE',
            userType: u.isCorporate ? 'CORPORATE' : 'PERSONAL',
          }));
          total = rawData.totalElements || finalUsers.length;
        } else if (isMultiSearch || (filterType === "name" && filterValue) || (!filterType && !filterValue)) {
          finalUsers = rawData.users || (Array.isArray(rawData) ? rawData : []);
          total = (rawData.totalElements || (rawData.totalPages || 0) * (rawData.pageSize || 20)) || finalUsers.length;
        } else if (filterType && filterValue) {
          finalUsers = Array.isArray(rawData) ? rawData : [rawData];
          total = finalUsers.length;
        }

        setUsers(finalUsers);
        setPagination(prev => ({
          ...prev,
          current: params.current || prev.current,
          total: total
        }));
        setHasSearched(true);
      } else {
        setUsers([]);
        NotificationManager.error(res?.message || "Failed to fetch users");
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      setUsers([]);
    } finally {
      setLoading(false);
      hideLoader();
    }
  }, [selectedUserType, pagination.pageSize, dateRange, status, kycStatus, category, tierLevel]);

  const handleTableChange = (pag) => {
    fetchUsers(pag, searchValue ? searchType : null, searchValue || null);
  };

  const handleActivation = async (userId, isActive) => {
    const { value: reason } = await Swal.fire({
      title: `Reason for ${isActive ? "activation" : "deactivation"}`,
      input: "text",
      inputPlaceholder: "Enter reason...",
      showCancelButton: true,
      inputValidator: (value) => !value && "Reason is required!"
    });

    if (reason) {
      showLoader();
      const response = await httpPut(
        `${customBaseUrl.authUrl}/admin/users/${userId}/active?isActive=${isActive}&reason=${reason}`,
        {}
      );
      hideLoader();
      if (response.success || response.status) {
        NotificationManager.success(`User ${isActive ? "activated" : "deactivated"}!`);
        fetchUsers({ current: pagination.current }, searchValue ? searchType : null, searchValue || null);
      } else {
        NotificationManager.error(response?.message || "Operation failed");
      }
    }
  };

  const handleUnlockAccount = async (userId) => {
    Swal.fire({
      title: "Unlock Account?",
      text: "This will unlock the user's account and allow them to log in.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ff6700",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Unlock",
    }).then(async (result) => {
      if (result.value) {
        showLoader();
        try {
          const res = await httpPost(`${customBaseUrl.authUrl}/api/v1/admin/users/lock-toggle/un-lock?userId=${userId}`, {});
          if (res?.status || res?.success) {
            NotificationManager.success("Account unlocked successfully");
            fetchUsers({ current: pagination.current }, searchValue ? searchType : null, searchValue || null);
          } else {
            NotificationManager.error(res?.message || "Failed to unlock account");
          }
        } catch (err) {
          NotificationManager.error("An error occurred");
        } finally {
          hideLoader();
        }
      }
    });
  };

  const handleCreateUser = async (values, accountType) => {
    showLoader();
    const endpoint =
      accountType === "personal"
        ? "/api/v1/admin/users/create-personal-account"
        : "/api/v1/admin/users/create-non-cac-account";

    try {
      const res = await httpPost(`${customBaseUrl.authUrl}${endpoint}`, {
        ...values,
        phoneNumber: `+234${values.phoneNumber.slice(-10)}`,
      });
      if (res?.success) {
        NotificationManager.success("User created successfully!");
        setShowCreateModal(false);
        fetchUsers({ current: 1 });
      } else {
        NotificationManager.error(res?.message || `Failed to create ${accountType} user.`);
      }
    } catch (error) {
      console.error(`Error creating ${accountType} user:`, error);
      NotificationManager.error("An error occurred while creating the user.");
    } finally {
      hideLoader();
    }
  };

  const handleSearch = () => {
    fetchUsers({ current: 1 }, searchValue ? searchType : null, searchValue || null);
  };

  const handleReset = () => {
    setSearchValue("");
    setSearchType("phoneNumber");
    setSelectedUserType(lockedUserType || "");
    setStatus(null);
    setKycStatus(null);
    setCategory(null);
    setTierLevel(null);
    setDateRange(null);
    setUsers([]);
    setPagination(prev => ({ ...prev, total: 0, current: 1 }));
    setHasSearched(false);
  };

  const handleExport = async () => {
    showLoader();
    try {
      const selectedType = USER_TYPES.find(t => t.value === selectedUserType);
      const payload = {
        ...(selectedType?.userType && { userType: selectedType.userType }),
        ...(selectedType?.businessType && { businessType: selectedType.businessType }),
        [searchType]: searchValue,
        status,
        kycStatus,
        customerCategory: category,
        tierLevel
      };
      if (dateRange) {
        payload.createdDateFrom = dateRange[0].format('YYYY-MM-DDT00:00:00');
        payload.createdDateTo = dateRange[1].format('YYYY-MM-DDT23:59:59');
      }
      const res = await httpPostBlob(`${customBaseUrl.authUrl}/admin/users/export`, payload);
      if (res) {
        const url = window.URL.createObjectURL(new Blob([res], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `Customer_Export_${moment().format('YYYYMMDD_HHmmss')}.csv`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        NotificationManager.success("Export successful!");
      } else {
        NotificationManager.error("Export failed");
      }
    } catch (e) {
      NotificationManager.error("An error occurred during export");
    } finally {
      hideLoader();
    }
  };

  const handleAction = (action, record) => {
    const id = record.id || record.userId;
    switch (action) {
      case "View Details":
        navigate(`/all-users/${id}`);
        break;
      case "Unlock Account":
        handleUnlockAccount(id);
        break;
      case "Activate":
        handleActivation(id, true);
        break;
      case "Deactivate":
        handleActivation(id, false);
        break;
      default:
        break;
    }
  };

  const columns = [
    { title: "First Name", dataIndex: "firstName", key: "firstName" },
    { title: "Last Name", dataIndex: "lastName", key: "lastName" },
    { title: "Email Address", dataIndex: "email", key: "email", ellipsis: true },
    { title: "Phone Number", dataIndex: "phoneNumber", key: "phoneNumber" },
    {
      title: "User Type",
      dataIndex: "userType",
      key: "userType",
      width: 120,
      render: (val) => val ? (
        <Tag color="blue" style={{ borderRadius: '6px', fontWeight: 600, fontSize: '11px' }}>
          {val}
        </Tag>
      ) : '—',
    },
    {
      title: "Business Type",
      dataIndex: "businessType",
      key: "businessType",
      ellipsis: true,
      render: (val) => val ? (
        <Tag color="purple" style={{ borderRadius: '6px', fontWeight: 600, fontSize: '11px' }}>
          {val}
        </Tag>
      ) : '—',
    },
    {
      title: "Category",
      dataIndex: "customerCategory",
      key: "customerCategory",
      render: (val) => val ? (
        <Tag color="cyan" style={{ borderRadius: '6px', fontWeight: 600, fontSize: '11px' }}>
          {val}
        </Tag>
      ) : '—',
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "status",
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? "success" : "error"} style={{ borderRadius: '10px' }}>
          {isActive ? "ACTIVE" : "INACTIVE"}
        </Tag>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 150,
      ellipsis: true,
      render: (createdAt) => createdAt ? moment(createdAt).format("YYYY-MM-DD HH:mm") : '—',
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <AntDropdown
          dropDownOptions={[
            "View Details",
            "Unlock Account",
            record.isActive ? "Deactivate" : "Activate"
          ]}
          handleDropdownAction={(action) => handleAction(action, record)}
        />
      ),
    },
  ];

  const selectedOption = SEARCH_OPTIONS.find(opt => opt.value === searchType);

  return (
    <Layout>
      <div className="all-users-wrapper" style={{ padding: '24px' }}>
        <PageHeadingBanner
          icon={<UserOutlined />}
          title="Customer Management"
          subtitle="Search, filter and manage all platform customers"
          actions={
            <Button
              icon={<UserAddOutlined />}
              onClick={() => setShowCreateModal(true)}
              style={{
                height: '42px',
                borderRadius: '8px',
                background: '#ffffff',
                color: '#ff6700',
                borderColor: '#ffffff',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Create New User
            </Button>
          }
        />

        {/* Filter Form Card */}
        <Card
          style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
          bodyStyle={{ padding: '20px 24px' }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>

            {/* User Type — hidden when locked (e.g. Individual submenu) */}
            {!lockedUserType && (
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>User Type</label>
                <AntSelect
                  value={selectedUserType}
                  onChange={setSelectedUserType}
                  style={{ width: '100%', height: '38px' }}
                >
                  {USER_TYPES.map(t => (
                    <Option key={t.value} value={t.value}>{t.label}</Option>
                  ))}
                </AntSelect>
              </div>
            )}

            {/* Search By */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Search By</label>
              <AntSelect
                value={searchType}
                onChange={setSearchType}
                style={{ width: '100%', height: '38px' }}
              >
                {SEARCH_OPTIONS.map(opt => (
                  <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                ))}
              </AntSelect>
            </div>

            {/* Search Value */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Search Value</label>
              <Input
                placeholder={selectedOption?.placeholder || "Search..."}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onPressEnter={handleSearch}
                style={{ height: '38px' }}
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              />
            </div>

            {/* Account Status */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Account Status</label>
              <AntSelect
                value={status}
                onChange={setStatus}
                placeholder="All Statuses"
                allowClear
                style={{ width: '100%', height: '38px' }}
              >
                {userStatuses.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
              </AntSelect>
            </div>

            {/* KYC Status */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>KYC Status</label>
              <AntSelect
                value={kycStatus}
                onChange={setKycStatus}
                placeholder="All KYC"
                allowClear
                style={{ width: '100%', height: '38px' }}
              >
                {kycStatuses.map(s => <Option key={s.value} value={s.value}>{s.label}</Option>)}
              </AntSelect>
            </div>

            {/* Category */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Category</label>
              <AntSelect
                value={category}
                onChange={setCategory}
                placeholder="All Categories"
                allowClear
                style={{ width: '100%', height: '38px' }}
              >
                {customerCategories.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
              </AntSelect>
            </div>

            {/* Tier Level */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Tier Level</label>
              <AntSelect
                value={tierLevel}
                onChange={setTierLevel}
                placeholder="All Tiers"
                allowClear
                style={{ width: '100%', height: '38px' }}
              >
                {tierLevels.map(t => <Option key={t.value} value={t.value}>{t.label}</Option>)}
              </AntSelect>
            </div>

            {/* Date Range */}
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#555', marginBottom: '6px' }}>Date Range</label>
              <DatePicker.RangePicker
                style={{ width: '100%', height: '38px' }}
                onChange={(dates) => setDateRange(dates)}
                value={dateRange}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={handleReset}
              style={{ height: '38px', borderRadius: '8px' }}
            >
              Reset
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              style={{ height: '38px', borderRadius: '8px', color: '#ff6700', borderColor: '#ff6700' }}
            >
              Export
            </Button>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              style={{ height: '38px', borderRadius: '8px', background: '#001529', borderColor: '#001529' }}
            >
              Search
            </Button>
          </div>
        </Card>

        <div className="user-table-card">
          <Table
            columns={columns}
            dataSource={users}
            loading={loading}
            pagination={{
              ...pagination,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} customers`
            }}
            onChange={handleTableChange}
            rowKey="id"
            className="user-table"
          />
        </div>

        <CreateUserModal
          visible={showCreateModal}
          onCancel={() => setShowCreateModal(false)}
          onCreate={handleCreateUser}
        />
      </div>

      <style>{`
        .ant-btn {
          cursor: pointer !important;
        }
        .ant-btn:disabled {
          cursor: not-allowed !important;
        }
      `}</style>
    </Layout>
  );
};

export default AllUsers;
